import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { SquaresFour, Users, CalendarCheck, AirplaneTilt, Money, ChartBar, Megaphone, GearSix, Handshake, SignOut, List, X, Files, LockKey, ClockCounterClockwise } from "@phosphor-icons/react";

const nav = [
  { to: "/admin", label: "Dashboard", icon: SquaresFour, end: true },
  { to: "/admin/employees", label: "Employees", icon: Users },
  { to: "/admin/attendance", label: "Attendance", icon: CalendarCheck },
  { to: "/admin/leave", label: "Leave", icon: AirplaneTilt },
  { to: "/admin/payroll", label: "Payroll", icon: Money },
  { to: "/admin/fnf", label: "Full & Final", icon: Handshake },
  { to: "/admin/documents", label: "Documents", icon: Files },
  { to: "/admin/reports", label: "Reports", icon: ChartBar },
  { to: "/admin/announcements", label: "Announcements", icon: Megaphone },
  { to: "/admin/audit-log", label: "Audit trail", icon: ClockCounterClockwise, superOnly: true },
  { to: "/admin/settings", label: "Settings", icon: GearSix },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const visibleNav = useMemo(() => nav.filter((n) => !n.superOnly || user?.role === "super_admin"), [user]);

  const links = (
    <nav className="flex-1 px-3 space-y-1">
      {visibleNav.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
          data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
          className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-colors duration-200 ${isActive ? "bg-accent text-primary" : "text-stone-600 hover:bg-stone-100"}`}>
          <n.icon size={20} weight="duotone" />{n.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden lg:flex flex-col w-64 border-r border-stone-200 bg-stone-50 fixed inset-y-0">
        <div className="p-6">
          <img src="/dcn-logo.jpg" alt="DCN Food and Beverages" className="h-12 w-auto rounded-md" data-testid="admin-logo" />
          <div className="text-xs text-stone-500 mt-2 tracking-wide uppercase">Employer Portal</div>
        </div>
        {links}
        <div className="p-4 border-t border-stone-200">
          <div className="text-sm font-semibold text-stone-800" data-testid="admin-user-name">{user?.name}</div>
          <div className="text-xs text-stone-500 mb-3">{user?.role === "super_admin" ? "Super Admin" : "HR / Admin"}</div>
          <button onClick={() => navigate("/change-password")} data-testid="admin-change-password-button" className="flex items-center gap-2 text-sm text-stone-600 hover:text-primary transition-colors duration-200">
            <LockKey size={18} /> Change password
          </button>
          <button onClick={doLogout} data-testid="admin-logout-button" className="flex items-center gap-2 text-sm text-stone-600 hover:text-primary transition-colors duration-200">
            <SignOut size={18} /> Sign out
          </button>
        </div>
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setOpen(false)}>
          <aside className="w-64 h-full bg-stone-50 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 flex items-center justify-between">
              <div className="font-heading font-extrabold text-lg">DCN <span className="text-primary">Foods</span></div>
              <button onClick={() => setOpen(false)} data-testid="admin-close-menu"><X size={22} /></button>
            </div>
            {links}
            <div className="p-4 border-t border-stone-200">
              <button onClick={() => { setOpen(false); navigate("/change-password"); }} data-testid="admin-change-password-mobile" className="flex items-center gap-2 text-sm text-stone-600"><LockKey size={18} /> Change password</button>
              <button onClick={doLogout} data-testid="admin-logout-mobile" className="flex items-center gap-2 text-sm text-stone-600"><SignOut size={18} /> Sign out</button>
            </div>
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 backdrop-blur-xl bg-white/80 border-b border-stone-200 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setOpen(true)} data-testid="admin-open-menu"><List size={24} /></button>
          <div className="font-heading font-extrabold"><img src="/dcn-logo.jpg" alt="DCN" className="h-8 w-auto rounded" /></div>
          <div className="w-6" />
        </header>
        <main className="p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
